import { useEffect } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Analytics } from "@vercel/analytics/react";
import { HelmetProvider } from "react-helmet-async";
import ClarityProvider from "@/components/ClarityProvider";
import DebugUserBanner from "@/components/DebugUserBanner";
import ErrorBoundary from "@/components/ErrorBoundary";
import GTMProvider from "@/components/GoogleAnalytics";
import MetaTags from "@/components/MetaTags";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleProtectedRoute from "@/components/RoleProtectedRoute";
import TrackingProvider from "@/components/TrackingProvider";
import WinnerModalManager from "@/components/WinnerModalManager";
import BulkAnalysisPage from "@/components/bulk-analysis/BulkAnalysisPage";
import FilterPanelDemo from "@/components/bulk-analysis/FilterPanelDemo";
import CarouselBanner from "@/components/carousel-banner";
import RacingLayout from "@/components/layouts/RacingLayout";
import Navbar from "@/components/navbar";
import { Toaster } from "@/components/ui/toaster";
import NotFoundPage from "@/pages/404";
import AnalysisDatePage from "@/pages/app/analysis-date";
import AnalysisOverview from "@/pages/app/analysis-overview";
import AnalysisSlugRouter from "@/pages/app/analysis-slug-router";
import AuthCallback from "@/pages/app/auth-callback";
import CheckoutSuccessPage from "@/pages/app/checkout-success";
import ComingSoon from "@/pages/app/coming-soon";
import ConfirmSso from "@/pages/app/confirm-sso";
import DailyWrap from "@/pages/app/daily-wrap";
import Dashboard from "@/pages/app/dashboard";
import EdmViewer from "@/pages/app/edm-viewer";
import FilterTestPage from "@/pages/app/filter-test";
import HomePage from "@/pages/app/home-page";
import Leaderboard from "@/pages/app/leaderboard";
// /live retired (owner, 1 Nov 2026: not used any more). Page + components kept in src for now.
// import LiveTips from "@/pages/app/live-tips";
import MagicLinkVerify from "@/pages/app/magic-link-verify";
import MyTips from "@/pages/app/my-tips";
import OnboardingPage from "@/pages/app/onboarding";
import PaymentSuccess from "@/pages/app/payment-success";
import PerformanceDetail from "@/pages/app/performance-detail";
import PerformanceSummary from "@/pages/app/performance-summary";
import Results from "@/pages/app/results";
import Signup from "@/pages/app/signup";
import SingleSso from "@/pages/app/single-sso";
import SocialPreviews from "@/pages/app/social-previews";
import StateResults from "@/pages/app/state-results";
import StripePaymentLinkRedirect from "@/pages/app/stripe-payment-link-redirect";
import TodaysTips from "@/pages/app/todays-tips";
import TrackResults from "@/pages/app/track-results";
import UrlMappings from "@/pages/app/url-mappings";
import VipPage from "@/pages/app/vip";
import WinningTip from "@/pages/app/winning-tip";
import Confirm from "@/pages/confirm";
import ConfirmationSuccess from "@/pages/confirmation-success";
import LandingPage from "@/pages/landing";
import PrivacyPolicy from "@/pages/landing/privacy";
import ShortRedirect from "@/pages/landing/short-redirect";
import SubscriptionAgreement from "@/pages/landing/subscription-agreement";
import LexiProLandingPage from "@/pages/lexi-pro";
import MediaKit from "@/pages/media-kit";
import OptOutPromo from "@/pages/opt-out-promo";
import RaceResultsPage from "@/pages/race-results";
import Analysis from "@/pages/temp/analysis";
import Unsubscribe from "@/pages/unsubscribe";
import { ReqRoles } from "./utils/constants";

// Import dashboard pages - temporarily disabled
// import Dashboard from "@/pages/dashboard";
// import StrategyDetail from "@/pages/dashboard/strategy/[id]";
// import CreateStrategy from "@/pages/dashboard/create-strategy";
// import DataView from "@/pages/dashboard/data";
// import SportsData from "@/pages/dashboard/sports-data";
// import TeamDetails from "@/pages/dashboard/team-details";

const queryClient = new QueryClient();

// Create router options with future flags
const routerOptions = {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true,
  },
};

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter {...routerOptions}>
          <MetaTags />
          <GTMProvider />
          <ClarityProvider />
          <Analytics />
          <TrackingProvider>
            {/* Global winner modal manager */}
            <WinnerModalManager />
            <Toaster />
            <DebugUserBanner />

            <Routes>
              <Route
                path="/"
                element={
                  <>
                    <Navbar />
                    <LandingPage />
                  </>
                }
              />
              <Route path="/lexi-pro" element={<LexiProLandingPage />} />
              <Route path="/race-results" element={<RaceResultsPage />} />
              <Route path="/wins" element={<Navigate to="/" replace />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route
                path="/subscription-agreement"
                element={<SubscriptionAgreement />}
              />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/opt-out-promo" element={<OptOutPromo />} />
              <Route path="/media-kit" element={<MediaKit />} />
              <Route path="/confirm" element={<Confirm />} />
              <Route
                path="/confirmation-success"
                element={
                  <>
                    <Navbar />
                    <ConfirmationSuccess />
                  </>
                }
              />
              <Route
                path="/leaderboard"
                element={
                  <ProtectedRoute>
                    <Leaderboard />
                  </ProtectedRoute>
                }
              />

              {/* Racing pages with shared layout */}
              <Route
                element={
                  <ProtectedRoute>
                    <RacingLayout />
                  </ProtectedRoute>
                }
              >
                <Route
                  path="/home"
                  element={
                    <ProtectedRoute>
                      <HomePage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/vip" element={<VipPage />} />
                <Route path="/todays-tips" element={<TodaysTips />} />
                <Route path="/my-tips" element={<MyTips />} />
              </Route>

              {/* Live Tips desktop dashboard: retired 1 Nov 2026 (owner: not used any more).
              <Route
                path="/live"
                element={
                  <ProtectedRoute>
                    <LiveTips />
                  </ProtectedRoute>
                }
              />
              */}

              <Route path="/coming-soon" element={<ComingSoon />} />
              {/* <Route
                path="/ask"
                element={
                  <ProtectedRoute>
                    <AskLexi />
                  </ProtectedRoute>
                }
              /> */}
              {/* Stripe checkout success route - handles post-payment flow */}
              <Route
                path="/checkout/success"
                element={<CheckoutSuccessPage />}
              />

              {/* Post-checkout completion route - redirects authenticated users to home */}
              <Route
                path="/checkout/complete"
                element={
                  <ProtectedRoute>
                    <Navigate to="/home" replace />
                  </ProtectedRoute>
                }
              />

              {/* Checkout error/cancel route */}
              <Route
                path="/checkout/error"
                element={
                  <>
                    <Navbar />
                    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
                      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
                        <h2 className="text-xl font-semibold mb-4">
                          Checkout Issue
                        </h2>
                        <p className="text-gray-600 mb-6">
                          There was an issue with your checkout. Please try
                          again or contact support.
                        </p>
                        <button
                          onClick={() => (window.location.href = "/")}
                          className="bg-[#F0A7D6] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#e391c4] transition-colors"
                        >
                          Return to Home
                        </button>
                      </div>
                    </div>
                  </>
                }
              />

              {/* Stripe payment link redirect route - handles redirect after Stripe payment link checkout */}
              <Route
                path="/payment-link/redirect"
                element={<StripePaymentLinkRedirect />}
              />

              {/* Legacy payment success route - redirects to login */}
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/signup" element={<Signup />} />
              {/* Legacy route redirect */}
              <Route
                path="/lexi-social-signup"
                element={<Navigate to="/signup" replace />}
              />
              <Route path="/login" element={<SingleSso />} />
              <Route path="/auth/magic-link" element={<MagicLinkVerify />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/confirm-sso" element={<ConfirmSso />} />
              {/* Lexi Performance — internal, desktop-only, unlisted (URL-only). */}
              <Route
                path="/performance"
                element={
                  <ProtectedRoute>
                    <PerformanceSummary />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/performance/detail"
                element={
                  <ProtectedRoute>
                    <PerformanceDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onboard"
                element={
                  <ProtectedRoute>
                    <OnboardingPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/url-mappings"
                element={
                  ["dev.lexi.tips", "staging.lexi.tips", "lexi.tips"].includes(
                    typeof window !== "undefined"
                      ? window.location.hostname
                      : "",
                  ) ? (
                    <Navigate to="/" replace />
                  ) : (
                    <div className="home-page-container">
                      <div className="container mx-auto px-4 py-4 bg-white">
                        <UrlMappings />
                      </div>
                    </div>
                  )
                }
              />
              {/* <Route
                path="/all-tips"
                element={
                  <ProtectedRoute>
                    <AllTips />
                  </ProtectedRoute>
                }
              /> */}
              {/* Semi Protected Analysis Routes */}
              <Route path="/analysis/*" element={<AnalysisLayout />}>
                <Route index element={<AnalysisOverview />} />
                <Route
                  path="today"
                  element={<AnalysisDatePage defaultToToday />}
                />
                <Route path=":state/:trackName" element={<TrackResults />} />
                <Route path=":slug" element={<AnalysisSlugRouter />} />
              </Route>

              {/* Bulk Analysis Route */}
              <Route
                path="/bulk-analysis"
                element={
                  <ProtectedRoute>
                    <>
                      <Navbar />
                      <div className="home-page-container">
                        <div className="container mx-auto px-4 py-4 bg-white">
                          <BulkAnalysisPage />
                        </div>
                      </div>
                    </>
                  </ProtectedRoute>
                }
              />

              {/* Filter Demo Route (for testing) */}
              <Route
                path="/filter-demo"
                element={
                  <>
                    <Navbar />
                    <div className="home-page-container">
                      <div className="container mx-auto px-4 py-4 bg-white">
                        <FilterPanelDemo />
                      </div>
                    </div>
                  </>
                }
              />

              {/* Simple Filter Test Route (minimal dependencies) */}
              <Route
                path="/filter-test"
                element={
                  <>
                    <Navbar />
                    <div className="home-page-container">
                      <div className="container mx-auto px-4 py-4 bg-white">
                        <FilterTestPage />
                      </div>
                    </div>
                  </>
                }
              />

              {/* Protected Results Route */}
              <Route
                path="/results"
                element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <Results />
                    </ErrorBoundary>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/temp/analysis"
                element={
                  <ProtectedRoute>
                    <Analysis />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/app/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="/social-previews" element={<SocialPreviews />} />

              {/* EDM Viewer Route - Restricted to developers and admins */}
              <Route
                path="/edm-viewer"
                element={
                  <RoleProtectedRoute requiredRoles={ReqRoles}>
                    <EdmViewer />
                  </RoleProtectedRoute>
                }
              />

              {/* New unprotected winning tip route */}
              <Route path="/winning-tipz" element={<WinningTip />} />

              {/* New daily wrap route */}
              <Route path="/daily-wrap" element={<DailyWrap />} />

              {/* Dashboard Routes - temporarily disabled
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/strategy/:id"
                element={
                  <ProtectedRoute>
                    <StrategyDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/create-strategy"
                element={
                  <ProtectedRoute>
                    <CreateStrategy />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/data"
                element={
                  <ProtectedRoute>
                    <DataView />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/sports-data"
                element={
                  <ProtectedRoute>
                    <SportsData />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/dashboard/team/:teamName" 
                element={
                  <ProtectedRoute>
                    <TeamDetails />
                  </ProtectedRoute>
                }
              />
              */}
              {/* 404 Route - Must be last */}
              {/* Short redirect must be before 404 */}
              <Route path="/:shortCode" element={<ShortRedirect />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </TrackingProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

function AnalysisLayout() {
  const location = useLocation();
  const params = useParams();

  useEffect(() => {
    try {
      console.groupCollapsed(
        `[AnalysisLayout] navigation → ${location.pathname}`,
      );
      console.log("location", {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      });
      console.log(
        "derivedSegments",
        location.pathname.split("/").filter(Boolean),
      );
      console.log("routeParams", params);
      console.groupEnd();
    } catch (err) {
      console.log("[AnalysisLayout] log failure", err);
    }
  }, [location, params]);

  return (
    <>
      <Navbar />
      <div className="home-page-container">
        <div className="container mx-auto px-4 py-4 bg-white">
          <Outlet />
        </div>
      </div>
    </>
  );
}

export default App;

// test: commit 1
